import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import routes from 'constants/routes'
import FolderLogo from 'images/MDT_FolderLogo.png'
import { useSelector, useDispatch } from 'react-redux'
import { Calendar, Menu as MenuIcon, User, LogOut } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
} from 'components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from 'components/ui/tooltip'
import { AskMdtTrigger } from 'features/AskMdt/components/AskMdtTrigger'
import ChatPopupTrigger from 'features/Chat/popup/ChatPopupTrigger'
import { NotificationBell } from 'features/Notifications/components/NotificationBell'
import { logout } from 'slices/authSlice'
import useAppMode from 'features/Field/useAppMode'
import { fieldRoutes } from 'constants/fieldRoutes'
import { Smartphone } from 'lucide-react'

interface HeaderCNProps {
	onMobileMenuToggle?: () => void
	/**
	 * A screen's own control at the left end of the bar, right beside the left
	 * nav panel: Home's "Daily Briefing" (MYD-644). Absent on other screens.
	 */
	leading?: React.ReactNode
}

const HeaderCN = ({ onMobileMenuToggle, leading }: HeaderCNProps) => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const { setMode, canChoose } = useAppMode()

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo } = userLogin
	const { name, image } = userInfo || {}

	const now = new Date()
	const weekday = now.toLocaleDateString('en-US', { weekday: 'long' })
	const shortDate = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

	const handleLogout = () => {
		dispatch(logout())
		navigate('/login')
	}

	return (
		<header className="relative flex items-center justify-between h-14 px-4 border-b border-gray-200 bg-white flex-shrink-0">
			{/* Left: mobile hamburger */}
			<div className="flex items-center">
				<button
					type="button"
					onClick={onMobileMenuToggle}
					className="md:hidden flex items-center justify-center w-9 h-9 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
					aria-label="Toggle menu"
				>
					<MenuIcon className="w-5 h-5" />
				</button>
				{/* Below 768px the nav, and the logo in it, sits behind the hamburger,
				    so the header carries the folder mark instead (MYD-532). */}
				<Link to={routes.DESKTOP_HOME} className="md:hidden ml-2 flex items-center" aria-label="Home">
					<img src={FolderLogo} alt="MYDealTeams" className="h-7 w-auto" />
				</Link>
				{leading && <div className="ml-2 flex items-center md:-ml-2">{leading}</div>}
			</div>

			{/* Right: field mode + chat + date + avatar dropdown */}
			<div className="flex items-center gap-4">
				{/*
					Entry to the field app from the desktop site.

					Above a phone this is a MODE SWITCH: it stores a preference, so the
					choice survives and `/` keeps opening the app the user picked. A
					visible switch rather than a pixel threshold, because the device
					says what the hardware is, not what the user wants to do with it,
					and inferring it is what produced the bug where an iPad could reach
					a screen in landscape but not in portrait.

					On a phone there is no mode to switch: the field app IS mobile
					MYDealTeams, and `setMode` is a no-op there. The control stays
					anyway, as plain navigation, because a phone still reaches desktop
					URLs through shared links and bookmarks and this is the way back.
					Removing it outright would strand exactly the user this whole
					change is for.
				*/}
				<button
					type="button"
					onClick={() => {
						setMode('field')
						navigate(fieldRoutes.HOME)
					}}
					// Shown at EVERY width, label from `sm` up.
					//
					// It was `hidden sm:inline-flex`, which hid the only way back to
					// the field app on precisely the devices the field app is for.
					className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-2.5 sm:px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary"
					title={canChoose ? 'Open the field app, built for site visits' : 'Back to the field app'}
					aria-label={canChoose ? 'Open the field app' : 'Back to the field app'}
				>
					<Smartphone className="w-3.5 h-3.5" />
					<span className="hidden sm:inline">{canChoose ? 'Field mode' : 'Field app'}</span>
				</button>
				{/* Messages. Deliberately not `hidden sm:flex` like the date
				    beside it: chat is the one control here that matters on a
				    phone, and it is the only entry point to the global chat. */}
				{/* Ask MDT (MYD-621). Renders nothing unless it is offered to this user. */}
				<AskMdtTrigger />
				<ChatPopupTrigger />
				{/* Notifications (MYD-542), at every width for the same reason. */}
				<NotificationBell />

				{/* Date display */}
				<div className="hidden sm:flex items-center gap-2 text-sm text-gray-600">
					<Calendar className="w-4 h-4 text-gray-400" />
					<span className="font-medium">{weekday}</span>
					<span className="text-gray-400">|</span>
					<span>{shortDate}</span>
				</div>

				{/* Avatar dropdown */}
				<DropdownMenu>
					<TooltipProvider>
					<Tooltip>
						<TooltipTrigger asChild>
							<DropdownMenuTrigger asChild>
								<button type="button" className="rounded-full focus:outline-none">
									<Avatar className="w-9 h-9 cursor-pointer ring-2 ring-transparent hover:ring-[#0080fe]/20 transition-all">
										<AvatarImage src={image} />
										<AvatarFallback>{name ? name.charAt(0).toUpperCase() : '?'}</AvatarFallback>
									</Avatar>
								</button>
							</DropdownMenuTrigger>
						</TooltipTrigger>
						<TooltipContent side="bottom">User navigation</TooltipContent>
					</Tooltip>
					</TooltipProvider>
					<DropdownMenuContent align="end" className="w-44">
						<DropdownMenuItem onClick={() => navigate('/admin/userprofile')}>
							<User className="w-4 h-4 mr-2" />
							User Profile
						</DropdownMenuItem>
						<DropdownMenuSeparator />
						<DropdownMenuItem onClick={handleLogout}>
							<LogOut className="w-4 h-4 mr-2" />
							Logout
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</header>
	)
}

export default HeaderCN
